/**
 * Portfolio Sensitivity Map
 * Cross-holding exposure & FX scenario analysis for investors
 */

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, RefreshCw, Globe, FileDown, Sparkles } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Treemap } from 'recharts';

interface Scenario {
  label: string;
  ccy: string;
  delta: number;
}

interface PortfolioSensitivityMapProps {
  base?: string;
}

export default function PortfolioSensitivityMap({ base = 'USD' }: PortfolioSensitivityMapProps) {
  const [currExp, setCurrExp] = useState<any[]>([]);
  const [jurExp, setJurExp] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [scenarios, setScenarios] = useState<Scenario[]>([
    { label: '+5% USD', ccy: 'USD', delta: 5 },
    { label: '-10% ZAR', ccy: 'ZAR', delta: -10 },
  ]);
  const [result, setResult] = useState<any>(null);

  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expBase, setExpBase] = useState(base);

  async function load() {
    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast({ title: 'Error', description: 'User not authenticated', variant: 'destructive' });
        return;
      }

      const [
        { data: ce, error: ceError },
        { data: je, error: jeError },
        { data: cp, error: cpError },
      ] = await Promise.all([
        supabase.rpc('psm_currency_exposure', { p_user_id: user.id, p_base: expBase }),
        supabase.rpc('psm_jurisdiction_exposure', { p_user_id: user.id, p_base: expBase }),
        supabase.rpc('psm_company_positions', { p_user_id: user.id, p_base: expBase }),
      ]);

      if (ceError) throw ceError;
      if (jeError) throw jeError;
      if (cpError) throw cpError;

      setCurrExp(ce || []);
      setJurExp(je || []);
      setPositions(cp || []);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  async function runScenarios() {
    setRunning(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('psm-scenario-sim', {
        body: { userId: user.id, base: expBase, scenarios },
      });

      if (error) throw error;
      setResult(data);
      toast({ title: 'Success', description: 'Scenario simulation completed' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  }

  async function exportPdf() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('psm-export-pdf', {
        body: { userId: user.id, base: expBase, scenarios },
      });

      if (error) throw error;

      if (data?.html) {
        // Open HTML in new window for printing
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(data.html);
          printWindow.document.close();
          toast({
            title: 'Success',
            description: 'Report opened. Use browser print to save as PDF.',
          });
        }
      }
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  }

  useEffect(() => {
    load();
  }, [expBase]);

  const totalBase = useMemo(
    () => (positions || []).reduce((s: number, p: any) => s + (p.implied_value_base || 0), 0),
    [positions]
  );

  const updateScenario = (index: number, updates: Partial<Scenario>) => {
    setScenarios((prev) => prev.map((s, i) => (i === index ? { ...s, ...updates } : s)));
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Globe className="text-primary" /> Portfolio Sensitivity Map
        </h2>
        <div className="flex items-center gap-2">
          <Select value={expBase} onValueChange={setExpBase}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="ZAR">ZAR</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="GBP">GBP</SelectItem>
              <SelectItem value="AED">AED</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle>
            Total Value ({expBase}): {expBase} {totalBase.toLocaleString()}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          {/* Currency Exposure */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Currency Exposure</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={currExp}>
                  <XAxis dataKey="currency" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="implied_value_base" fill="#2563eb" name={`Value (${expBase})`} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          {/* Jurisdiction Exposure */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Jurisdiction Heatmap</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <Treemap
                  data={(jurExp || []).map((j: any) => ({
                    name: j.jurisdiction || '—',
                    size: j.implied_value_base || 0,
                  }))}
                  dataKey="size"
                  nameKey="name"
                  stroke="#fff"
                  fill="#10b981"
                />
              </ResponsiveContainer>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Positions */}
      <Card className="border-border bg-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Positions by Company</CardTitle>
          <Button size="sm" variant="outline" onClick={exportPdf}>
            <FileDown className="h-4 w-4 mr-1" /> Export PDF
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Jurisdiction</TableHead>
                <TableHead>Shares</TableHead>
                <TableHead>FMV / Share ({expBase})</TableHead>
                <TableHead className="text-right">Implied Value ({expBase})</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {positions.map((p: any) => (
                <TableRow key={p.company_id}>
                  <TableCell>{p.company_name}</TableCell>
                  <TableCell>{p.currency}</TableCell>
                  <TableCell>{p.jurisdiction || '—'}</TableCell>
                  <TableCell>{(p.shares || 0).toLocaleString()}</TableCell>
                  <TableCell>{(p.fmv_per_share_base || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    {expBase} {(p.implied_value_base || 0).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Scenario Builder */}
      <Card className="border-border bg-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Scenario Analysis (FX Shocks)</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setScenarios([
                  { label: '+5% USD', ccy: 'USD', delta: 5 },
                  { label: '-10% ZAR', ccy: 'ZAR', delta: -10 },
                ])
              }
            >
              Reset
            </Button>
            <Button size="sm" onClick={runScenarios} disabled={running}>
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-1" /> Run
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid sm:grid-cols-3 gap-3">
            {scenarios.map((s: Scenario, i: number) => (
              <div key={i} className="border p-2 rounded-md space-y-1">
                <div className="flex gap-2">
                  <Input
                    value={s.label}
                    onChange={(e) => updateScenario(i, { label: e.target.value })}
                  />
                  <Select value={s.ccy} onValueChange={(v) => updateScenario(i, { ccy: v })}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="ZAR">ZAR</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="AED">AED</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs w-16">Δ FX %</span>
                  <Input
                    type="number"
                    value={s.delta}
                    onChange={(e) => updateScenario(i, { delta: Number(e.target.value || 0) })}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Scenario Results */}
          {result?.overview && (
            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              {result.overview.map((r: any) => (
                <div key={r.label} className="border p-2 rounded-md">
                  <div className="font-medium">{r.label}</div>
                  <div>
                    Total Value: {expBase} {(r.total_value_base || 0).toLocaleString()}
                  </div>
                  <div>Δ vs Base: {(r.delta_pct || 0).toFixed(2)}%</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
